import { credentialingConfig } from '@/backend/features/credentialing/credentialingConfig';
import { AppContext } from '@/ui/Context';
import { appName } from '@/ui/constants';
import { BackgroundCheckAccountSection } from '@/ui/features/background-check';
import { useOverrideAccountFilter, useOverrideMapAccountFilterReverse } from '@/ui/features/contact-management/AccountsPanel/hooks';
import { useExtendedAccountsFilters } from '@/ui/features/contact-management/AccountsPanel/hooks/useExtendedAccountsFilters';
import { useExtendedUserDirectoryFilters, useOverrideUserDirectoryFilter } from '@/ui/features/contact-management/UserDIrectoryPanel/hooks';
import { useContactManagementCustomization } from '@juvare/alert-ui/ui/hooks/useContactManagementCustomization';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { AccountColumns } from '@juvare/contact-management/backend/features/accounts/shared';
import { AccountsTableCustomization } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountsTable';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { ContactManagementConfigProvider } from '@juvare/contact-management/ui/providers';
import {
    BulkVerifyCredentialsParams,
    verifyCredentialsBulkActionType,
} from '@juvare/credentialing/backend/features/credentialing-verification/shared';
import { allowRequestLicenseCheck } from '@juvare/credentialing/backend/features/permissions';
import { launchBulkAction } from '@juvare/platform-sdk-react/ui/features/bulk-actions';
import { Filter } from '@juvare/platform-shared/features/storage';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import React from 'react';
import { useTranslation } from 'react-i18next';

export const ContactManagementConfigProviderUser: React.FC<React.PropsWithChildren> = (props) => {
    const { notificationConfig, accountsTableCustomization } = useContactManagementCustomization();
    const { extendFilters: extendAccountFilters } = useExtendedAccountsFilters();
    const { extendFilters: extendUserDirectoryFilters } = useExtendedUserDirectoryFilters();
    const userState = useUserState();
    const { t } = useTranslation('rms');
    const { overrideMapAccountFilterReverse } = useOverrideMapAccountFilterReverse();

    const getDataTableProps: AccountsTableCustomization['getDataTableProps'] = (defaultDataTableProps, appliedFilterData, location) => {
        const dataTableProps = {
            ...(accountsTableCustomization.getDataTableProps?.(defaultDataTableProps, appliedFilterData, location) ??
                defaultDataTableProps),
        };

        dataTableProps.localActions = dataTableProps.localActions ?? [];
        const selectedRowIds = dataTableProps.selectedRowIds ?? [];
        const appliedStorageFilters = dataTableProps.customBackendResponseData?.storageClientRequest?.filter ?? [];
        const filter = (
            selectedRowIds.length > 0
                ? [
                      {
                          op: 'in',
                          id: selectedRowIds,
                      },
                  ]
                : appliedStorageFilters
        ) as Filter<AccountColumns>[];
        const verifyCredentialsButtonDataTestAttribute = 'verify-credentials-local-action-button';
        const handleRequestVerification = () => {
            try {
                launchBulkAction<BulkVerifyCredentialsParams>({
                    type: verifyCredentialsBulkActionType,
                    title: t('credentialing.labels.verifyCredentials'),
                    params: {
                        requestedBy: userState?.displayName,
                        filter: filter,
                        config: credentialingConfig,
                    },
                    onClose: () => {
                        dataTableProps.onDeselectAllRows?.();
                    },
                });
            } catch (error) {
                handleErrorDialog(error);
            }
        };

        if (allowRequestLicenseCheck(userState)) {
            const localActionIdx = dataTableProps.localActions.findIndex(
                (action) => action['data-test'] === verifyCredentialsButtonDataTestAttribute,
            );
            if (localActionIdx !== -1) {
                dataTableProps.localActions.splice(localActionIdx, 1);
            }

            dataTableProps.localActions.push({
                icon: <ContentPasteSearchIcon />,
                label: t('credentialing.labels.verifyCredentials'),
                'data-test': verifyCredentialsButtonDataTestAttribute,
                onClick: () => {
                    showConfirmDialog({
                        title: t('credentialing.titles.verifyCredentials'),
                        content: t('credentialing.verifyCredentialsConfirmation'),
                        onConfirm: () => handleRequestVerification(),
                        confirmLabel: t('common.verify'),
                    });
                },
            });
        }

        return dataTableProps;
    };

    const additionalAccountSectionPages = [
        {
            value: 'background-check-required-information',
            label: 'Background Check Required Information',
        },
    ];

    return (
        <ContactManagementConfigProvider
            appName={appName}
            appContext={AppContext}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            accountConfig={{ component: { backgroundCheck: BackgroundCheckAccountSection as any }, additionalAccountSectionPages }}
            notificationConfig={notificationConfig}
            accountsTableCustomization={{
                getDataTableProps,
                extendFilters: extendAccountFilters,
                overrideUseAccountFilter: useOverrideAccountFilter as AccountsTableCustomization['overrideUseAccountFilter'],
                overrideMapAccountFilterReverse:
                    overrideMapAccountFilterReverse as AccountsTableCustomization['overrideMapAccountFilterReverse'],
            }}
            userDirectoryTableCustomization={{
                extendFilters: extendUserDirectoryFilters,
                overrideUseAccountFilter: useOverrideUserDirectoryFilter,
            }}
        >
            {props.children}
        </ContactManagementConfigProvider>
    );
};
